import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {catchError, map, Observable, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {Entity, EntityKey} from '../entity/entity';
import {EntityLineHeader} from '../entity/entity-line-header';
import {EntitySaveResponse} from '../entity/entity-save-response';
import {PartSearchResult} from '../products/part-search-result';
import {GoogleService} from './google-service';

@Service()
export class QuoteService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);

  private urlBase = environment.serviceUrl;

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  public save(quote: Entity): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/save';
    let body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  public getAssociatedOrders(quoteId: string, revision: number): Observable<EntityKey[]> {
    let url = this.urlBase + 'quotes/associatedOrders/' + quoteId + '/' + revision;
    return this.http.get<EntityKey[]>(url, {headers: this.getRequestHeaders()});
  }

  public runMultiplier(quote: Entity): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/multiplier';
    let body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public createPartsQuote(parts: PartSearchResult): Observable<Entity> {
    let url = this.urlBase + 'quotes/createPartsQuote';
    let body = JSON.stringify(parts);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  public copy(key: EntityKey): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/copy';
    let body = JSON.stringify(key);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public copyLines(key: EntityKey, lines: EntityLineHeader[]): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/copyLines/' + key.entityId + '/' + key.entityRevision;
    let body = JSON.stringify(lines);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public createAndCopyLines(lines: EntityLineHeader[]): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/createAndCopyLines';
    let body = JSON.stringify(lines);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public createPaperPilot(key: EntityKey): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/createPaperPilot/' + key.entityId + '/' + key.entityRevision;
    return this.http.post<EntitySaveResponse>(url, null, {headers: this.getPostHeaders()});
  }

  placeOrder(quote: Entity): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/placeOrder';
    const body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public approveQuote(key: EntityKey): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/approveQuote';
    let body = JSON.stringify(key);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  isSentToEwl(key: EntityKey): Observable<boolean> {
    const url = this.urlBase + 'quotes/sentToEwl/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'}).pipe(map((result) => result === 'true'));
  }

  isRepriceNeeded(key: EntityKey): Observable<string> {
    let url = this.urlBase + 'quotes/repriceNeeded/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  reprice(key: EntityKey): Observable<Entity> {
    let url = this.urlBase + 'quotes/reprice';
    let body = JSON.stringify(key);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  getCreditDocumentsRequired(key: EntityKey): Observable<string[]> {
    let url = this.urlBase + 'quotes/creditDocumentsRequired/' + key.entityId + '/' + key.entityRevision;
    return this.http.get<string[]>(url, {headers: this.getRequestHeaders()});
  }

  startCreditDocumentsWorkflow(key: EntityKey): Observable<string> {
    let url = this.urlBase + 'quotes/startCreditDocumentWorkflow';
    let body = JSON.stringify(key);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  assignNewOpportunity(quote: Entity): Observable<EntitySaveResponse> {
    let url = this.urlBase + 'quotes/assignNewOpportunity';
    let body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

}
